/* eslint-disable sonarjs/cognitive-complexity */

import { captureException } from "@sentry/nextjs";
import { useConnection, useWallet } from "@solana/wallet-adapter-react";
import { LAMPORTS_PER_SOL } from "@solana/web3.js";
import type { Transaction } from "@solana/web3.js";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  INVALID_AMOUNT,
  constructLiquidityActionsTx,
} from "@utils/transactions/construct-liquidity-actions-tx";
import { useMarinade } from "contexts/MarinadeContext";
import { usePriorityFee } from "contexts/PriorityFeeContext";
import { useToast } from "contexts/ToastContext";
import { useWalletInfo } from "contexts/WalletInfoContext";
import { coinSymbols } from "services/domain/coinSymbols";
import type { ToastInfos } from "tw/components/molecules/Toast";
import { trackBeginCheckout, trackPurchase } from "utils/gtag";
import { shortenAddress } from "utils/shorten-address";
import { getErrorMessage } from "utils/web3/get-error-message";
import { handleTx } from "utils/web3/handle-transactions";

import { useMarinadeState } from "./useMarinadeState";

export const useLiquidityAction = (action: "deposit" | "withdraw") => {
  const { t } = useTranslation();
  const { connection } = useConnection();
  const wallet = useWallet();
  const { tokenAccounts, SOLBalance } = useWalletInfo();
  const { showToast, updateToast, isToastOpen } = useToast();
  const { marinade } = useMarinade();
  const marinadeState = useMarinadeState();
  const { priorityFeeLamports } = usePriorityFee();

  const [tx, setTx] = useState<Transaction>();
  const [inputAmount, setInputAmount] = useState<number>(0);
  const [lpAmountToReceive, setLpAmountToReceive] = useState<number>();
  const [solAmountToReceive, setSolAmountToReceive] = useState<number>();
  const [mSolAmountToReceive, setMSolAmountToReceive] = useState<number>();
  const [isLoading, setIsLoading] = useState(false);

  const balance = useMemo(() => {
    if (action === "deposit") {
      return SOLBalance ? SOLBalance / LAMPORTS_PER_SOL : undefined;
    }
    const lpToken = tokenAccounts.get(coinSymbols.lpSOL);
    return lpToken?.balance;
  }, [action, SOLBalance, tokenAccounts]);

  useEffect(() => {
    if (action === "deposit") {
      setSolAmountToReceive(undefined);
      setMSolAmountToReceive(undefined);
      setLpAmountToReceive(
        marinadeState
          ? inputAmount / marinadeState.liquidityPool.lpPrice.sol
          : 0
      );
    } else {
      setSolAmountToReceive(
        marinadeState
          ? (inputAmount * marinadeState.liquidityPool.solBalance.sol) /
              marinadeState.liquidityPool.supply.sol
          : 0
      );
      setMSolAmountToReceive(
        marinadeState
          ? (inputAmount * marinadeState.liquidityPool.mSolBalance.sol) /
              marinadeState.liquidityPool.supply.sol
          : 0
      );
      setLpAmountToReceive(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    inputAmount,
    action,
    marinadeState?.liquidityPool.lpPrice.sol,
    marinadeState?.liquidityPool.mSolBalance.sol,
    marinadeState?.liquidityPool.solBalance.sol,
  ]);

  useEffect(() => {
    const constructTx = async () => {
      if (!inputAmount || inputAmount <= INVALID_AMOUNT || !marinade) return;
      setIsLoading(true);

      try {
        const { transaction } = await constructLiquidityActionsTx(
          marinade,
          inputAmount,
          action
        );

        setTx(transaction);
      } catch {
        setTx(undefined);
      }
      setIsLoading(false);
    };
    constructTx();
  }, [marinade, action, inputAmount]);

  const execute = async (onSignature?: () => void) => {
    trackBeginCheckout(
      inputAmount,
      action === "deposit" ? t("liquidityPage.add") : t("liquidityPage.remove"),
      "liquidity"
    );

    if (!balance) return;

    try {
      if (inputAmount > balance) {
        throw new Error("insufficient funds for instruction");
      }

      let signature = "";

      if (tx) {
        const result = await handleTx(
          wallet,
          tx,
          connection,
          () => {
            if (onSignature) {
              onSignature();
            }
            showToast({
              status: "loading",
              title: t("app.transactions.sent-message"),
            });
          },
          priorityFeeLamports
        );
        signature = result.signature;
      } else {
        throw new Error("Transaction could not be generated");
      }

      updateToast({
        status: "success",
        title: t("app.transactions.tx-confirmed"),
        description: shortenAddress(signature),
      });

      trackPurchase(
        inputAmount * (action === "deposit" ? 1 : -1),
        action === "deposit"
          ? t("liquidityPage.add")
          : t("liquidityPage.remove"),
        "liquidity"
      );

      setInputAmount(0);
    } catch (err) {
      const message = getErrorMessage(err, t);
      captureException(err, {
        tags: {
          message,
        },
      });

      if (message) {
        const toastContent: ToastInfos = {
          status: "error",
          title: `${
            action === "deposit" ? "Depositing" : "Withdrawing"
          } SOL has failed`,
          description: message,
        };

        if (isToastOpen) {
          updateToast(toastContent);
        } else {
          showToast(toastContent);
        }
      }
    }
  };

  return {
    transaction: tx,
    isLoading,
    balance,
    execute,
    setInputAmount,
    lpAmountToReceive,
    solAmountToReceive,
    mSolAmountToReceive,
  };
};
